import { SlidersHorizontal } from "lucide-react";

import { usePortal } from "@/features/portal/portal-context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LivePreview } from "@/features/preview/live-preview";
import { ThemeStrip } from "@/features/theming/theme-strip";
import { ThemeStudioProvider } from "@/features/theming/theme-studio-context";
import { ThemeGenerator } from "@/features/theming/theme-generator";
import { ThemeCandidates } from "@/features/theming/theme-candidates";
import { ThemeKnobs } from "@/features/theming/theme-knobs";
import { AdvancedTokens } from "@/features/theming/advanced-tokens";

/**
 * Theming v2 — the 10-star flow: give us anything → 2-3 generated full themes on
 * the real app → 4 knobs → save & publish. Powered by the mirrored pop.in Theme
 * Studio engine. The old per-token grid is demoted to the Advanced drawer.
 *
 * Save/discard/publish/dirty are unchanged (top-bar owns them); the studio only
 * writes the draft's 6 hexes through the portal's existing `onThemeChange` seam.
 */
export function ThemingPage() {
  const { draft } = usePortal();

  return (
    <ThemeStudioProvider>
      <div className="grid grid-cols-1 items-start gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div>
          <h1 className="text-2xl font-extrabold tracking-[-.02em]">Theming</h1>
          <p className="mb-[26px] mt-1.5 max-w-[560px] text-sm text-muted-foreground">
            Give us anything — a website, your logo, or nothing — and we’ll generate full themes
            on your real app. Tune four knobs, then Save &amp; publish.
          </p>

          <ThemeStrip />

          <div className="mb-4 text-[13px] text-text-3">
            Editing <span className="font-semibold text-foreground">{draft.theme.name}</span>
          </div>

          <ThemeGenerator />
          <ThemeCandidates />

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>
                <SlidersHorizontal /> Tune it
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <ThemeKnobs />
            </CardContent>
          </Card>

          <AdvancedTokens />
        </div>

        <LivePreview />
      </div>
    </ThemeStudioProvider>
  );
}
